@if (isPopup()) {
  <div class="history-panel history-panel--popup">
    <div class="history-panel__new-chat-row">
      <button
        type="button"
        class="history-panel__new-chat history-panel__new-chat--popup"
        (click)="onNewChat()"
      >
        <span class="history-panel__plus">+</span>
        <span>{{ 'chatBot.history.newChat' | translateIn }}</span>
      </button>
    </div>

    <ng-container *ngTemplateOutlet="listContent" />
  </div>
} @else {
  <aside
    class="history-panel"
    [class.history-panel--collapsed]="historySidebarCollapsed()"
  >
    @if (!historySidebarCollapsed()) {
      <div class="history-panel__toolbar">
        <button
          type="button"
          class="history-panel__new-chat"
          puButton
          uiType="secondary"
          size="m"
          (click)="onNewChat()"
        >
          <span class="history-panel__plus">+</span>
          <span>{{ 'chatBot.history.newChat' | translateIn }}</span>
        </button>

        <button
          type="button"
          class="history-panel__sidebar-toggle"
          puButton
          uiType="quaternary"
          size="m"
          [onlyIcon]="true"
          [attr.aria-expanded]="true"
          [attr.aria-label]="'chatBot.history.collapseSidebar' | translateIn"
          (click)="toggleSidebar()"
        >
          <pu-icon iconName="pu-chevron-left" size="20" />
        </button>
      </div>

      <ng-container *ngTemplateOutlet="listContent" />
    }
  </aside>
}

@if (deletingChatId()) {
  <div class="history-panel__confirm-backdrop" (click)="cancelDelete()">
    <div class="history-panel__confirm-dialog" (click)="$event.stopPropagation()">
      <h4 class="history-panel__confirm-title">{{ 'chatBot.history.deleteTitle' | translateIn }}</h4>
      <p class="history-panel__confirm-body">{{ 'chatBot.history.deleteBody' | translateIn }}</p>
      <div class="history-panel__confirm-actions">
        <button
          type="button"
          class="history-panel__confirm-btn history-panel__confirm-btn--danger"
          (click)="confirmDelete()"
        >{{ 'chatBot.history.deleteConfirm' | translateIn }}</button>
        <button
          type="button"
          class="history-panel__confirm-btn"
          (click)="cancelDelete()"
        >{{ 'chatBot.history.deleteCancel' | translateIn }}</button>
      </div>
    </div>
  </div>
}

<ng-template #listContent>
  <div class="history-panel__body" (scroll)="onBodyScroll($event)">
    @if (hasError()) {
      <div class="history-panel__error">
        {{ 'chatBot.history.loadError' | translateIn }}
      </div>
    } @else if (chats().length === 0) {
      <div class="history-panel__empty">
        {{ 'chatBot.history.empty' | translateIn }}
      </div>
    } @else {
      <ul class="history-panel__list">
        @if (!selectedChatId()) {
          <li
            class="history-panel__item history-panel__item--draft history-panel__item--active"
            (click)="onNewChat()"
            (keydown.enter)="onNewChat()"
            (keydown.space)="$event.preventDefault(); onNewChat()"
            tabindex="0"
            role="button"
          >
            <span class="history-panel__item-icon">
              <pu-icon iconName="messages" size="20" />
            </span>
            <span class="history-panel__item-label">{{ 'chatBot.history.newChat' | translateIn }}</span>
          </li>
        }
        @for (chat of chats(); track chat.id) {
          <li
            class="history-panel__item"
            [class.history-panel__item--active]="selectedChatId() === chat.id"
          >
            @if (renamingChatId() === chat.id) {
              <div class="history-panel__item-rename">
                <input
                  #renameInput
                  class="history-panel__rename-input"
                  type="text"
                  [value]="chatDisplayTitle(chat)"
                  maxlength="100"
                  [attr.aria-label]="'chatBot.history.rename' | translateIn"
                  (keydown)="onRenameKeydown($event, chat.id, renameInput.value)"
                  autofocus
                />
                <button
                  type="button"
                  class="history-panel__rename-action history-panel__rename-action--confirm"
                  [attr.aria-label]="'chatBot.history.renameConfirm' | translateIn"
                  (mousedown)="$event.preventDefault()"
                  (click)="confirmRename(chat.id, renameInput.value)"
                >&#10003;</button>
                <button
                  type="button"
                  class="history-panel__rename-action history-panel__rename-action--cancel"
                  [attr.aria-label]="'chatBot.history.renameCancel' | translateIn"
                  (mousedown)="$event.preventDefault()"
                  (click)="cancelRename()"
                >&#10005;</button>
              </div>
            } @else {
              <button
                type="button"
                class="history-panel__item-button"
                (click)="onSelectChat(chat.id)"
              >
                <span class="history-panel__item-icon">
                  <pu-icon iconName="messages" size="20" />
                </span>
                <span
                  class="history-panel__item-label"
                  [title]="chatDisplayTitle(chat)"
                >{{ chatDisplayTitle(chat) }}</span>
                @if (generatingChatId() === chat.id) {
                  <span class="history-panel__generating-dot" aria-hidden="true"></span>
                }
                @if (workChatBadge(chat); as badge) {
                  <span
                    class="history-panel__work-context-badge"
                    [title]="badge"
                  >{{ badge }}</span>
                }
                @if (chat.isPinned) {
                  <span class="history-panel__pin-indicator" aria-hidden="true">
                    <pu-icon iconName="pinned" size="14" />
                  </span>
                }
              </button>
              <button
                type="button"
                class="history-panel__item-action"
                puButton
                uiType="quaternary"
                size="s"
                [onlyIcon]="true"
                [attr.aria-label]="'chatBot.history.actions' | translateIn"
                [puPopover]="chatActionsPopover"
                [position]="['br', 'bl']"
                [offsetY]="3"
                scrollStrategy="close"
                (click)="$event.stopPropagation()"
              >
                <pu-icon iconName="dots" size="16" />
              </button>

              <ng-template
                #chatActionsPopover
                let-close="close"
              >
                <div class="history-panel__actions-popover" (click)="close()">
                  <button
                    type="button"
                    class="history-panel__actions-popover-item"
                    (click)="togglePin($event, chat.id, chat.isPinned); close()"
                  >
                    <pu-icon [iconName]="chat.isPinned ? 'pinned-off' : 'pinned'" size="16" />
                    <span>{{ (chat.isPinned ? 'chatBot.history.unpin' : 'chatBot.history.pin') | translateIn }}</span>
                  </button>
                  <button
                    type="button"
                    class="history-panel__actions-popover-item"
                    (click)="startRename($event, chat.id, chatDisplayTitle(chat)); close()"
                  >
                    <pu-icon iconName="pu-pencil" size="16" />
                    <span>{{ 'chatBot.history.rename' | translateIn }}</span>
                  </button>
                  <button
                    type="button"
                    class="history-panel__actions-popover-item history-panel__actions-popover-item--danger"
                    (click)="requestDelete($event, chat.id); close()"
                  >
                    <pu-icon iconName="pu-close" size="12" />
                    <span>{{ 'chatBot.history.delete' | translateIn }}</span>
                  </button>
                </div>
              </ng-template>
            }
          </li>
        }
      </ul>
      @if (isLoadingMore()) {
        <div class="history-panel__load-more-spinner">
          <ng-container *puSpinner="true" />
        </div>
      }
    }
  </div>
</ng-template>
